/**
 * LES CONTRAINTES D'UN TRACÉ, EN PHRASES — chantier « Élève », lot 8, palier
 * L0 (« voir avant de produire », AB#943 ; `docs/pilotage/atelier-figures/
 * CONCEPTION.md`, § 5.1).
 *
 * En relecture, un tracé se lisait « 3 objets attendus, 2 contraintes
 * vérifiées ». Le relecteur ne pouvait pas savoir CE QUI est vérifié. Ici, la
 * liste des contraintes de l'attendu devient des phrases :
 *
 *   « La droite doit passer par (3 ; 2) et avoir une pente de −1. »
 *
 * ─── Pourquoi ici ───────────────────────────────────────────────────────────
 *
 * La console de relecture les lit (L0). La rétroaction de figure chez l'élève
 * (L1) dira les mêmes : « ce que tu as réussi », « la chose à retravailler ».
 * Une phrase écrite deux fois finirait par dire deux choses de la même
 * contrainte. Fonction PURE : ni modèle, ni horloge.
 *
 * ─── La forme ───────────────────────────────────────────────────────────────
 *
 * Chaque contrainte donne un PRÉDICAT à l'infinitif (« avoir une pente de −1 »)
 * et un SUJET (la cible qu'elle mesure : « la droite », « le point A »). Les
 * contraintes d'un même sujet se rassemblent en une phrase, dans l'ordre du
 * corrigé. Une contrainte sans cible pour sujet (l'angle entre trois points)
 * fait sa phrase seule. Les nombres sont écrits à la française (virgule, signe
 * moins typographique), les points « (3 ; 2) ».
 */
import { estIdEnonce } from './construction.js';
import { regleFonctionV2 } from './decrire-v2.js';
import { fonctionCanoniqueV2 } from './figure-v2.js';
import { formaterNombre } from './geometrie.js';
/* ================================================================ les mots */
/** Un nombre à la française, le signe moins typographique. */
export function nombreEnPhrase(v) {
    return formaterNombre(v).replace(/^-/u, '−');
}
const NOM_DU_GENRE = {
    point: { nom: 'point', feminin: false },
    ligne: { nom: 'droite', feminin: true },
    droite: { nom: 'droite', feminin: true },
    demi_droite: { nom: 'demi-droite', feminin: true },
    segment: { nom: 'segment', feminin: false },
    cercle: { nom: 'cercle', feminin: false },
    polygone: { nom: 'polygone', feminin: false },
    fonction: { nom: 'courbe', feminin: true },
    par_parties: { nom: 'courbe', feminin: true },
    demi_plan: { nom: 'demi-plan', feminin: false },
    vecteur: { nom: 'vecteur', feminin: false },
};
const ORDINAUX = ['première', 'deuxième', 'troisième', 'quatrième', 'cinquième'];
const ORDINAUX_M = ['premier', 'deuxième', 'troisième', 'quatrième', 'cinquième'];
const estObjet = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const nombre = (v) => typeof v === 'number' && Number.isFinite(v) ? v : null;
/** « la droite », « le point A », « la deuxième droite » — le nom d'une cible, avec son article. */
function nomDeCible(id, cibles) {
    const c = cibles.find((x) => x.id === id);
    if (c === undefined)
        return `l’objet « ${id} »`;
    const g = NOM_DU_GENRE[c.genre] ?? { nom: c.genre, feminin: false };
    const article = g.feminin ? 'la' : 'le';
    const debut = /^[aeiouyéèh]/iu.test(g.nom) ? 'l’' : `${article} `;
    if (c.nom !== undefined)
        return `${debut}${g.nom} ${c.nom}`;
    const memes = cibles.filter((x) => x.nom === undefined && (NOM_DU_GENRE[x.genre]?.nom ?? x.genre) === g.nom);
    if (memes.length <= 1)
        return `${debut}${g.nom}`;
    const rang = memes.indexOf(c);
    const ordinal = (g.feminin ? ORDINAUX : ORDINAUX_M)[rang] ?? `n° ${rang + 1}`;
    return `${article} ${ordinal} ${g.nom}`;
}
/** Un objet de l'énoncé : « le point A », ou « l'objet 4 de l'énoncé » (« e:3 »). */
function nomDEnonce(ref) {
    if (estIdEnonce(ref)) {
        const rang = Number(ref.slice(2));
        return Number.isInteger(rang) ? `l’objet ${rang + 1} de l’énoncé` : ref.slice(2);
    }
    return `le point ${ref}`;
}
/** « (3 ; 2) ». */
export function pointEnPhrase(x, y) {
    return `(${nombreEnPhrase(x)} ; ${nombreEnPhrase(y)})`;
}
/** Une référence (`Ref`) : des coordonnées, un objet de l'énoncé, une cible. */
function nomDeRef(r, cibles) {
    if (!estObjet(r))
        return 'un objet non désigné';
    const x = nombre(r.x);
    const y = nombre(r.y);
    if (x !== null && y !== null)
        return pointEnPhrase(x, y);
    if (typeof r.enonce === 'string')
        return nomDEnonce(r.enonce);
    if (typeof r.cible === 'string')
        return nomDeCible(r.cible, cibles);
    return 'un objet non désigné';
}
/** « y = 2x + 1 », lue par la forme canonique de la famille — la même que la courbe tracée. */
function regleEnPhrase(famille, parametres) {
    const r = fonctionCanoniqueV2(famille, parametres);
    return 'fonction' in r ? `y = ${regleFonctionV2(r.fonction)}` : `une fonction ${String(famille)}`;
}
/** « [−2 ; 1[ » : un bout plein se ferme, un bout vide s'ouvre. */
function intervalleEnPhrase(domaine, bouts) {
    const g = bouts?.[0] === 'vide' ? ']' : '[';
    const d = bouts?.[1] === 'vide' ? '[' : ']';
    return `${g}${nombreEnPhrase(domaine[0])} ; ${nombreEnPhrase(domaine[1])}${d}`;
}
const estDomaine = (v) => Array.isArray(v) && v.length === 2 && nombre(v[0]) !== null && nombre(v[1]) !== null;
/** La transformation d'une contrainte `image`. */
function transformationEnPhrase(t, cibles) {
    if (!estObjet(t))
        return 'une transformation non décrite';
    switch (t.type) {
        case 'translation': {
            const x = nombre(t.x);
            const y = nombre(t.y);
            if (x !== null && y !== null)
                return `la translation de vecteur ${pointEnPhrase(x, y)}`;
            return `la translation qui envoie ${nomDeRef(t.de, cibles)} sur ${nomDeRef(t.a, cibles)}`;
        }
        case 'reflexion':
            return `la réflexion d’axe ${nomDeRef(t.axe, cibles)}`;
        case 'rotation': {
            const a = nombre(t.angle) ?? 0;
            const sens = a < 0 ? 'horaire' : 'antihoraire';
            return `la rotation de centre ${nomDeRef(t.centre, cibles)}, de ${nombreEnPhrase(Math.abs(a))}° dans le sens ${sens}`;
        }
        case 'homothetie':
            return `l’homothétie de centre ${nomDeRef(t.centre, cibles)} et de rapport ${nombreEnPhrase(nombre(t.rapport) ?? 0)}`;
        default:
            return 'une transformation non décrite';
    }
}
/* ================================================================ sujet et prédicat */
/** La référence que la contrainte MESURE : son sujet. */
function refDuSujet(c) {
    switch (c.type) {
        case 'pente':
        case 'ordonnee_origine':
        case 'droite_par':
        case 'passe_par':
        case 'perpendiculaire':
        case 'parallele':
        case 'mediatrice':
        case 'bissectrice':
            return c.ligne;
        /* L3 (F3) : pour `intersection` et `equidistant` aussi, le point construit est le sujet. */
        case 'point_en':
        case 'milieu':
        case 'intersection':
        case 'equidistant':
            return c.point;
        case 'sur':
            /* Un point TRACÉ sur un objet : le point est le sujet. Un point donné : l'objet. */
            return estObjet(c.point) && typeof c.point.cible === 'string' ? c.point : c.objet;
        case 'rayon':
            return c.cercle;
        case 'aire':
            return c.polygone;
        case 'longueur':
            return c.segment;
        case 'angle':
            return null;
        default:
            return c.objet;
    }
}
/** Le sujet d'une contrainte : l'identifiant de sa cible, ou `null` (phrase seule). */
export function sujetDeContrainte(c) {
    const r = refDuSujet(c);
    return estObjet(r) && typeof r.cible === 'string' ? r.cible : null;
}
/**
 * Le PRÉDICAT d'une contrainte, à l'infinitif : « avoir une pente de −1 »,
 * « passer par (3 ; 2) ». Ce qui suit « doit ».
 */
export function predicatDeContrainte(c, cibles) {
    const ref = (r) => nomDeRef(r, cibles);
    /** « de » + la référence, contracté : « du point A », « de la droite », « de l’objet 3 ». */
    const de = (r) => {
        const n = ref(r);
        if (n.startsWith('le '))
            return `du ${n.slice(3)}`;
        if (n.startsWith('les '))
            return `des ${n.slice(4)}`;
        return `de ${n}`;
    };
    const v = nombre(c.valeur);
    switch (c.type) {
        case 'pente':
            return `avoir une pente de ${nombreEnPhrase(v ?? 0)}`;
        case 'ordonnee_origine':
            return `avoir une ordonnée à l’origine de ${nombreEnPhrase(v ?? 0)}`;
        case 'droite_par':
            return `passer par ${ref(c.p1)} et par ${ref(c.p2)}`;
        case 'passe_par':
            return `passer par ${ref(c.point)}`;
        case 'point_en': {
            const x = nombre(c.x);
            const y = nombre(c.y);
            return x === null || y === null ? 'être placé' : `être en ${pointEnPhrase(x, y)}`;
        }
        case 'sur':
            return sujetDeContrainte(c) !== null && estObjet(c.point) && typeof c.point.cible === 'string'
                ? `être sur ${ref(c.objet)}`
                : `passer par ${ref(c.point)}`;
        case 'rayon':
            return `avoir un rayon de ${nombreEnPhrase(v ?? 0)}`;
        case 'aire':
            return `avoir une aire de ${nombreEnPhrase(v ?? 0)}`;
        case 'longueur':
            return `mesurer ${nombreEnPhrase(v ?? 0)}`;
        case 'courbe_egale': {
            const sur = estDomaine(c.domaine) ? ` sur ${intervalleEnPhrase(c.domaine)}` : '';
            return `être la courbe de ${regleEnPhrase(c.famille, c.parametres)}${sur}`;
        }
        case 'caracteristiques': {
            /* L6 (F8) : ce que l'esquisse doit montrer, dit comme la règle le donne. */
            const dits = [];
            if (estObjet(c.sommet)) {
                const x = nombre(c.sommet.x);
                const y = nombre(c.sommet.y);
                if (x !== null && y !== null)
                    dits.push(`avoir son sommet en ${pointEnPhrase(x, y)}`);
            }
            if (c.ouverture === 'haut' || c.ouverture === 'bas')
                dits.push(`s’ouvrir vers le ${c.ouverture}`);
            if (Array.isArray(c.zeros)) {
                const zs = c.zeros.map(nombre).filter((z) => z !== null);
                dits.push(zs.length === 0
                    ? 'ne pas couper l’axe des x'
                    : `couper l’axe des x en ${zs.map((z) => `x = ${nombreEnPhrase(z)}`).join(' et ')}`);
            }
            if (Array.isArray(c.sens)) {
                dits.push(`être ${c.sens.map((x) => (x === 'decroissante' ? 'décroissante' : 'croissante')).join(' puis ')}`);
            }
            if (estObjet(c.asymptotes)) {
                const h = nombre(c.asymptotes.horizontale);
                const v = nombre(c.asymptotes.verticale);
                if (h !== null)
                    dits.push(`avoir l’asymptote horizontale y = ${nombreEnPhrase(h)}`);
                if (v !== null)
                    dits.push(`avoir l’asymptote verticale x = ${nombreEnPhrase(v)}`);
            }
            return dits.length === 0 ? 'être esquissée' : dits.join(', ');
        }
        case 'parties_egales': {
            const parties = Array.isArray(c.parties) ? c.parties.filter(estObjet) : [];
            const dites = parties.map((p) => {
                const bouts = Array.isArray(p.bouts) && p.bouts.length === 2
                    ? p.bouts
                    : undefined;
                const sur = estDomaine(p.domaine) ? ` sur ${intervalleEnPhrase(p.domaine, bouts)}` : '';
                return `${regleEnPhrase(p.famille, p.parametres)}${sur}`;
            });
            return `être définie par parties : ${dites.join(' ; ')}`;
        }
        case 'demi_plan': {
            const p = estObjet(c.parametres) ? c.parametres : {};
            const frontiere = regleEnPhrase('lineaire', { a: p.a, b: p.b ?? 0 });
            const cote = c.cote === 'dessous' ? 'au-dessous' : 'au-dessus';
            const bord = c.stricte === true ? 'la frontière exclue, en pointillé' : 'la frontière comprise';
            return `être le demi-plan ${cote} de la droite ${frontiere}, ${bord}`;
        }
        case 'vecteur_egal': {
            const x = nombre(c.x);
            const y = nombre(c.y);
            return x === null || y === null
                ? 'avoir les composantes demandées'
                : `avoir pour composantes ${pointEnPhrase(x, y)}`;
        }
        case 'image':
            return `être l’image de ${ref(c.source)} par ${transformationEnPhrase(c.transformation, cibles)}`;
        case 'milieu':
            return `être le milieu ${de(c.de)} et ${de(c.a)}`;
        case 'perpendiculaire':
            return `être perpendiculaire à ${ref(c.a)}`;
        case 'parallele':
            return `être parallèle à ${ref(c.a)}`;
        case 'mediatrice':
            return `être la médiatrice du segment qui joint ${ref(c.de)} et ${ref(c.a)}`;
        case 'bissectrice':
            return `être la bissectrice de l’angle de sommet ${ref(c.sommet)}, entre ${ref(c.de)} et ${ref(c.a)}`;
        case 'angle':
            return `mesurer ${nombreEnPhrase(v ?? 0)}°`;
        /* ── L3 (F3) ── */
        case 'intersection': {
            const objets = Array.isArray(c.objets) ? c.objets : [];
            return `être à l’intersection ${de(objets[0])} et ${de(objets[1])}`;
        }
        case 'equidistant':
            return `être à égale distance ${de(c.de)} et ${de(c.a)}`;
        case 'isometrique':
            return `être isométrique à ${ref(c.source)}`;
        case 'semblable': {
            const k = nombre(c.rapport);
            return k === null
                ? `être semblable à ${ref(c.source)}`
                : `être semblable à ${ref(c.source)}, dans le rapport ${nombreEnPhrase(k)}`;
        }
        case 'nomme':
            return typeof c.nom === 'string' ? `porter le nom ${c.nom}` : 'porter le nom demandé';
        default:
            return `tenir la contrainte « ${c.type} »`;
    }
}
const majuscule = (t) => t.charAt(0).toUpperCase() + t.slice(1);
/** « a, b et c ». */
function enumerer(parties) {
    if (parties.length <= 1)
        return parties.join('');
    return `${parties.slice(0, -1).join(', ')} et ${parties[parties.length - 1]}`;
}
/** Une contrainte seule, en une phrase : « La droite doit avoir une pente de −1. » */
export function phraseDeContrainte(c, cibles) {
    if (c.type === 'angle') {
        const v = nombre(c.valeur) ?? 0;
        return `L’angle de sommet ${nomDeRef(c.sommet, cibles)}, entre ${nomDeRef(c.de, cibles)} et ${nomDeRef(c.a, cibles)}, doit mesurer ${nombreEnPhrase(v)}°.`;
    }
    const r = refDuSujet(c);
    const sujet = r === null || r === undefined ? 'Le tracé' : majuscule(nomDeRef(r, cibles));
    return `${sujet} doit ${predicatDeContrainte(c, cibles)}.`;
}
/**
 * Toutes les contraintes, en phrases : celles d'un même sujet n'en font
 * qu'une, dans l'ordre du corrigé (« La droite doit passer par (3 ; 2) et
 * avoir une pente de −1. »).
 */
export function phrasesDesContraintes(contraintes, cibles) {
    const groupes = [];
    contraintes.forEach((c, i) => {
        const sujet = c.type === 'angle' ? null : sujetDeContrainte(c);
        const deja = sujet === null ? undefined : groupes.find((g) => g.sujet === sujet);
        if (deja !== undefined)
            deja.rangs.push(i);
        else
            groupes.push({ sujet, rangs: [i] });
    });
    return groupes.map((g) => {
        const premiere = contraintes[g.rangs[0]];
        if (g.sujet === null) {
            return { texte: phraseDeContrainte(premiere, cibles), contraintes: g.rangs, sujet: null };
        }
        const predicats = g.rangs.map((i) => predicatDeContrainte(contraintes[i], cibles));
        return {
            texte: `${majuscule(nomDeCible(g.sujet, cibles))} doit ${enumerer(predicats)}.`,
            contraintes: g.rangs,
            sujet: g.sujet,
        };
    });
}
//# sourceMappingURL=contraintes-en-phrases.js.map